// Practice Details — defaults + formatters. Live values come from
// GET /api/practice-details via PracticeContext; these defaults keep first
// paint correct before the request resolves.

export const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const DEFAULT_PRACTICE = {
  address: {
    street: '121 Westminster Bridge Road',
    city: 'London',
    postcode: 'SE1 7HR',
    country: 'United Kingdom',
  },
  geo: { lat: 51.499128, lng: -0.113256 },
  hours: [
    { day: 'Monday', closed: true, open: '', close: '' },
    { day: 'Tuesday', closed: false, open: '10:15', close: '13:30' },
    { day: 'Wednesday', closed: false, open: '08:00', close: '19:00' },
    { day: 'Thursday', closed: false, open: '10:00', close: '20:00' },
    { day: 'Friday', closed: false, open: '08:00', close: '19:00' },
    { day: 'Saturday', closed: true, open: '', close: '' },
    { day: 'Sunday', closed: true, open: '', close: '' },
  ],
  announcement_enabled: true,
};

let snapshot = DEFAULT_PRACTICE;
export const getPracticeSnapshot = () => snapshot;
export const setPracticeSnapshot = (p) => { snapshot = p; };

export const formatAddress = (p = snapshot) =>
  `${p.address.street}, ${p.address.city}, ${p.address.postcode}`;

const DEFAULT_ADDRESS_TEXT = '121 Westminster Bridge Road, London, SE1 7HR';

// Generated copy names the address at writing time; keep it in step with Practice Details.
export const withLiveAddress = (text, p = snapshot) =>
  (text || '').split(DEFAULT_ADDRESS_TEXT).join(formatAddress(p));

export const postcodeArea = (p = snapshot) => (p.address.postcode || '').split(' ')[0];

export const formatTime = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'pm' : 'am';
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')}${suffix}`;
};

export const formatHours = (h) => (h.closed ? 'Closed' : `${formatTime(h.open)} – ${formatTime(h.close)}`);

export const openDaysSummary = (hours) => {
  const idx = hours.map((h, i) => (h.closed ? -1 : i)).filter((i) => i >= 0);
  const abbr = (i) => DAYS[i].slice(0, 3);
  if (!idx.length) return { count: 0, label: 'By appointment' };
  const contiguous = idx[idx.length - 1] - idx[0] === idx.length - 1;
  const label = idx.length === 1 ? abbr(idx[0])
    : contiguous ? `${abbr(idx[0])} – ${abbr(idx[idx.length - 1])}` : idx.map(abbr).join(', ');
  return { count: idx.length, label };
};

export const toPostalAddressLD = (p = snapshot) => ({
  '@type': 'PostalAddress',
  streetAddress: p.address.street,
  addressLocality: p.address.city,
  addressRegion: 'Greater London',
  postalCode: p.address.postcode,
  addressCountry: 'GB',
});

export const toGeoLD = (p = snapshot) => ({
  '@type': 'GeoCoordinates',
  latitude: p.geo.lat,
  longitude: p.geo.lng,
});

export const toOpeningHoursLD = (p = snapshot) =>
  p.hours.filter((h) => !h.closed).map((h) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: h.day,
    opens: h.open,
    closes: h.close,
  }));

export const mapsLinks = (p = snapshot) => {
  const q = encodeURIComponent(`${formatAddress(p)}, UK`);
  return {
    directions: `https://www.google.com/maps/dir/?api=1&destination=${q}`,
    embed: `https://maps.google.com/maps?q=${q}&hl=en&z=16&output=embed`,
    citymapper:
      `https://citymapper.com/directions?endcoord=${p.geo.lat},${p.geo.lng}` +
      `&endname=The+TMD+Physio&endaddress=${q}`,
  };
};

// index.html ships a static @graph that wins over runtime JSON-LD (see its
// dedupe script), so patch its address / geo / hours in place.
const STATIC_CLINIC_IDS = ['/#clinic', '/#krina-physician-practice'];

export const patchStaticGraph = (p) => {
  const el = document.querySelector('script[type="application/ld+json"]:not([data-rh]):not([id])');
  if (!el) return;
  try {
    const data = JSON.parse(el.textContent || '{}');
    if (!Array.isArray(data['@graph'])) return;
    data['@graph'].forEach((node) => {
      const id = node['@id'] || '';
      if (!STATIC_CLINIC_IDS.some((s) => id.endsWith(s))) return;
      node.address = toPostalAddressLD(p);
      if (id.endsWith('/#clinic')) {
        node.geo = toGeoLD(p);
        node.openingHoursSpecification = toOpeningHoursLD(p);
      }
    });
    el.textContent = JSON.stringify(data);
  } catch (_) { /* leave the static block untouched */ }
};
